import React, { useCallback, useEffect, useRef, useState } from "react";
import { Badge, Button, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { formatIndianPhone } from "../../components/PhoneLink";
import {
  getSilenceCheck,
  listSilenceAlerts,
  markSilenceAlertSeen,
  setSilenceAlertsOff,
} from "../../../services/tripTrackingApi";

// The server checks every minute; polling as often keeps the page at most one check behind.
const POLL_MS = 60_000;
const SOUNDED_KEY = "tripSilenceAlertsSounded";
// The statuses the server watches; the switch is noise on the rest.
const SWITCH_STATUSES = new Set(["UPCOMING", "ONGOING"]);

const readSounded = (storageKey) => {
  try { return new Set(JSON.parse(localStorage.getItem(storageKey) || "[]")); } catch { return new Set(); }
};
const writeSounded = (storageKey, sounded) => {
  try { localStorage.setItem(storageKey, JSON.stringify([...sounded].slice(-200))); } catch { /* storage unavailable */ }
};

// The page sounds when the office is emailed: once for the alert and once for the reminder an hour on.
// Remembered per browser, so a reload or a second tab does not sound the same moment again.
const soundKey = (entry) => `${entry.id}:${entry.remindedAtIst ? "reminder" : "alert"}`;

let audioContext = null;

function audio() {
  if (audioContext) return audioContext;
  const Context = window.AudioContext || window.webkitAudioContext;
  if (!Context) return null;
  try { audioContext = new Context(); } catch { audioContext = null; }
  return audioContext;
}

// Browsers keep sound off until someone has clicked on the page, and resume() then waits for that
// click instead of failing - so it gets a moment, and the state is read afterwards.
async function soundReady() {
  const context = audio();
  if (!context) return null;
  if (context.state !== "running") {
    await Promise.race([
      context.resume().catch(() => {}),
      new Promise((resolve) => { window.setTimeout(resolve, 300); }),
    ]);
  }
  return context.state === "running" ? context : null;
}

// Three two-tone chirps, about two seconds. Made in the browser, so there is no sound file to host.
async function playAlarm() {
  const context = await soundReady();
  if (!context) return false;
  const start = context.currentTime + 0.05;
  for (let round = 0; round < 3; round += 1) {
    [880, 660].forEach((frequency, step) => {
      const at = start + round * 0.6 + step * 0.22;
      const tone = context.createOscillator();
      const volume = context.createGain();
      tone.type = "square";
      tone.frequency.value = frequency;
      volume.gain.setValueAtTime(0.0001, at);
      volume.gain.exponentialRampToValueAtTime(0.2, at + 0.02);
      volume.gain.exponentialRampToValueAtTime(0.0001, at + 0.2);
      tone.connect(volume);
      volume.connect(context.destination);
      tone.start(at);
      tone.stop(at + 0.22);
    });
  }
  return true;
}

// Two soft rising notes, about a second: "time to do something", not the alarm.
export async function playChime() {
  const context = await soundReady();
  if (!context) return false;
  const start = context.currentTime + 0.05;
  [660, 880].forEach((frequency, step) => {
    const at = start + step * 0.28;
    const tone = context.createOscillator();
    const volume = context.createGain();
    tone.type = "sine";
    tone.frequency.value = frequency;
    volume.gain.setValueAtTime(0.0001, at);
    volume.gain.exponentialRampToValueAtTime(0.18, at + 0.03);
    volume.gain.exponentialRampToValueAtTime(0.0001, at + 0.6);
    tone.connect(volume);
    volume.connect(context.destination);
    tone.start(at);
    tone.stop(at + 0.62);
  });
  return true;
}

/**
 * Sounds the alarm for alerts that have not sounded in this browser, unless an admin already clicked
 * Seen; a sound the browser held back plays on the next click anywhere on the page. Each kind of
 * alert keeps its own {@code storageKey}; {@code play} is the sound, the alarm by default.
 */
export function useAlarmSound(alerts, storageKey, play = playAlarm) {
  const [soundBlocked, setSoundBlocked] = useState(false);

  const sound = useCallback(async (list) => {
    const due = list.filter((entry) => !entry.seenAtIst && !readSounded(storageKey).has(soundKey(entry)));
    if (!due.length) {
      setSoundBlocked(false);
      return;
    }
    if (!(await play())) {
      setSoundBlocked(true);
      return;
    }
    setSoundBlocked(false);
    const sounded = readSounded(storageKey);
    due.forEach((entry) => sounded.add(soundKey(entry)));
    writeSounded(storageKey, sounded);
  }, [storageKey, play]);

  useEffect(() => { void sound(alerts); }, [alerts, sound]);

  useEffect(() => {
    if (!soundBlocked) return undefined;
    const unlock = () => { void sound(alerts); };
    window.addEventListener("pointerdown", unlock, { once: true });
    return () => window.removeEventListener("pointerdown", unlock);
  }, [soundBlocked, alerts, sound]);

  return { soundBlocked, enableSound: () => sound(alerts) };
}

/**
 * The open "no location" alerts, polled while Manage Bookings is open. Sounds at the alert and at
 * its reminder unless an admin already clicked Seen, and says so when an alert clears.
 */
export function useSilenceAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [now, setNow] = useState(() => Date.now());
  const [seenBusy, setSeenBusy] = useState(null);
  // The all-trips check, for the "alerts are off" note; it is switched on Tracking Settings.
  const [check, setCheck] = useState(null);
  // The alerts from the last poll that answered; null until the first, so a page load says nothing.
  const previous = useRef(null);

  useEffect(() => {
    getSilenceCheck().then(setCheck).catch(() => setCheck({ unavailable: true }));
  }, []);

  const refresh = useCallback(async () => {
    try {
      const next = await listSilenceAlerts();
      const ids = new Set(next.map((entry) => entry.id));
      (previous.current || []).forEach((entry) => {
        if (ids.has(entry.id)) return;
        // The server also closes an alert quietly when the trip ends or its alerts are turned off, so
        // this does not claim more.
        toast.success(`Booking #${entry.bookingId}: alert cleared - location is back, the trip has ended or its alerts were turned off.`);
      });
      previous.current = next;
      setAlerts(next);
      setNow(Date.now());
    } catch {
      // The next poll retries; what is on screen stays.
    }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => { void refresh(); }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [refresh]);

  const { soundBlocked, enableSound } = useAlarmSound(alerts, SOUNDED_KEY);

  const markSeen = useCallback(async (entry) => {
    setSeenBusy(entry.id);
    try {
      const updated = await markSilenceAlertSeen(entry.id);
      setAlerts((current) => current.map((item) => (item.id === updated.id ? updated : item)));
    } catch (error) {
      window.alert(error?.response?.data?.message || "Could not mark the alert as seen.");
    } finally {
      setSeenBusy(null);
    }
  }, []);

  // Turning a trip's alerts off closes its alert on the server; it goes from here at once, without
  // the "cleared" toast.
  const dropBooking = useCallback((bookingId) => {
    const keep = (list) => list.filter((entry) => entry.bookingId !== bookingId);
    if (previous.current) previous.current = keep(previous.current);
    setAlerts(keep);
  }, []);

  return { alerts, now, soundBlocked, seenBusy, markSeen, dropBooking, enableSound, check };
}

export function clock(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const time = date.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" });
  return date.toDateString() === new Date().toDateString()
    ? time
    : `${date.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}, ${time}`;
}

function minutesSince(value, now) {
  const at = new Date(value).getTime();
  return Number.isFinite(at) ? Math.max(0, Math.round((now - at) / 60000)) : 0;
}

function duration(minutes) {
  return minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} hr ${minutes % 60} min`;
}

function silenceText(entry, now) {
  const minutes = duration(minutesSince(entry.silentSinceIst, now));
  return entry.kind === "NOT_STARTED"
    ? `Pickup was at ${clock(entry.silentSinceIst)} and the driver has not started sharing location (${minutes}).`
    : `No location for ${minutes} - last at ${clock(entry.silentSinceIst)}.`;
}

/** Marks the booking's row in the table, in the column with the tracking summary. */
export function SilenceBadge({ entry, now }) {
  if (!entry) return null;
  return (
    <Badge bg="danger" className="mb-1">
      <i className="bi bi-broadcast me-1" aria-hidden="true" />
      {entry.kind === "NOT_STARTED" ? "Not sharing" : `No location ${duration(minutesSince(entry.silentSinceIst, now))}`}
    </Badge>
  );
}

/**
 * In the Quick views bar only while the all-trips check is off: with no alerts possible, an empty
 * page must not read as all clear. The switch itself lives on Tracking Settings.
 */
export function SilenceCheckOffNote({ check }) {
  if (!check || check.unavailable || check.on) return null;
  return (
    <div className="booking-silence-check" role="status">
      <i className="bi bi-bell-slash" aria-hidden="true" />
      <span className="small fw-semibold">
        Location and not-ready alerts are off for all trips{check.changedBy ? ` (by ${check.changedBy}, ${clock(check.changedAtIst)})` : ""}.
        {" "}Turn them on in Tracking Settings.
      </span>
    </div>
  );
}

/**
 * The switch in a booking's Tracking & Actions cell. Off stops the trip's no-location emails and page
 * alarm until someone turns it back on; the trip is still tracked and marked as usual.
 */
export function SilenceAlertsSwitch({ booking, onChanged }) {
  const [busy, setBusy] = useState(false);
  if (!SWITCH_STATUSES.has(booking.tripStatus)) return null;
  const off = Boolean(booking.silenceAlertsOffAt);

  const change = async (on) => {
    if (!on && !window.confirm(`Turn off location alerts for booking #${booking.id}?\n\nThe office will not be emailed or alerted on this page if the driver's location stops.`)) return;
    setBusy(true);
    try {
      onChanged(await setSilenceAlertsOff(booking.id, !on));
    } catch (error) {
      window.alert(error?.response?.data?.message || "Could not change the location alerts.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-2">
      <Form.Check
        type="switch"
        id={`booking-${booking.id}-silence-alerts`}
        label="Location alerts"
        checked={!off}
        disabled={busy}
        onChange={(event) => change(event.target.checked)}
      />
      {off && (
        <div className="small text-danger">
          Off - by {booking.silenceAlertsOffBy || "admin"}, {clock(booking.silenceAlertsOffAt)}
        </div>
      )}
    </div>
  );
}

/** The red box under the status cards: one line per trip with no location. */
export function SilenceAlertPanel({ alerts, now, soundBlocked, seenBusy, onSeen, onEnableSound, onOpenTimeline, canOpenTimeline }) {
  if (!alerts.length) return null;
  return (
    <div className="booking-silence-alerts" role="alert">
      <div className="booking-silence-alerts-head">
        <span><i className="bi bi-broadcast me-1" aria-hidden="true" />Ongoing trips with no location ({alerts.length})</span>
        {soundBlocked && (
          <Button size="sm" variant="light" onClick={onEnableSound}>
            <i className="bi bi-volume-up me-1" aria-hidden="true" />Turn on sound
          </Button>
        )}
      </div>
      {alerts.map((entry) => {
        const { display, dial } = formatIndianPhone(entry.driverPhone);
        return (
          <div key={entry.id} className={`booking-silence-alert${entry.seenAtIst ? " seen" : ""}`}>
            <div className="booking-silence-alert-text">
              <div className="fw-semibold">
                #{entry.bookingId} · {entry.tripTitle || "Trip"}{entry.customerName ? ` · ${entry.customerName}` : ""}
              </div>
              <div>{silenceText(entry, now)}</div>
              {entry.reason && <div className="small">{entry.reason}</div>}
              <div className="small">
                Driver: {entry.driverName || "-"}{display ? ` · ${display}` : ""}
                {" · "}Office emailed {clock(entry.alertedAtIst)}
                {entry.remindedAtIst ? `, reminder ${clock(entry.remindedAtIst)}` : ""}
              </div>
            </div>
            <div className="booking-silence-alert-actions">
              {dial && (
                <Button size="sm" variant="danger" href={`tel:${dial}`}>
                  <i className="bi bi-telephone-fill me-1" aria-hidden="true" />Call driver
                </Button>
              )}
              {canOpenTimeline(entry.bookingId) && (
                <Button size="sm" variant="outline-danger" onClick={() => onOpenTimeline(entry.bookingId)}>
                  Open timeline
                </Button>
              )}
              {entry.lastLatitude != null && entry.lastLongitude != null && (
                <Button
                  size="sm"
                  variant="outline-danger"
                  href={`https://www.google.com/maps/search/?api=1&query=${entry.lastLatitude},${entry.lastLongitude}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Last location
                </Button>
              )}
              {entry.seenAtIst ? (
                <span className="booking-silence-seen">Seen by {entry.seenBy || "admin"} at {clock(entry.seenAtIst)}</span>
              ) : (
                <Button
                  size="sm"
                  variant="dark"
                  disabled={seenBusy === entry.id}
                  title="Stops the sound for every admin and shows them you are handling it"
                  onClick={() => onSeen(entry)}
                >
                  {seenBusy === entry.id ? <Spinner size="sm" /> : "Seen"}
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
